/**
 * Новая заявка — /requests/new?service={itemId} (canon design-guest §16).
 *
 * Отдельный экран (deep-link, не модал): summary-card услуги (название ·
 * категория · цена/бесплатно), варианты — pill-buttons с ценой отдельной
 * строкой (IBM Plex Mono, nowrap), общие поля: фото до 3 (через
 * /v1/files/presign + PUT в MinIO), priority-toggle «Срочно», комментарий.
 *
 * «Другое» (?category={code}&service=other) — generic-форма с ОБЯЗАТЕЛЬНЫМ
 * textarea описания (canon §16, блок «Другое»).
 *
 * Паттерны (BINDING): V4 inline tooltip + V5 server summary; skeleton на
 * загрузке; Friendly-тон, «Вы» с заглавной.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, Skeleton } from '@hsc/ui';
import {
  displayItemPrice,
  displayVariantPrice,
  ServiceItemId,
  ServiceItemVariantId,
  type CatalogViewCategory,
  type CatalogViewItem,
  type ServiceRequestCreateInput,
  type ServiceRequestCreatedResponse,
} from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useCreateServiceRequest, useServiceCatalog } from './useCatalog.js';
import { usePhotoUpload } from './usePhotoUpload.js';

/** Цель заявки, разрезолвленная из query-параметров по каталогу. */
interface RequestTarget {
  category: CatalogViewCategory;
  /** undefined — generic-форма «Другое» (свободный текст). */
  item?: CatalogViewItem;
}

const formSchema = z.object({
  variant_id: z.string().optional(),
  comment: z.string().max(1000, 'Комментарий — не больше 1000 символов').optional(),
  description: z.string().max(2000, 'Описание — не больше 2000 символов').optional(),
});

type FormValues = z.infer<typeof formSchema>;

/** Строка цены для summary-card: цена / «бесплатно» / null (скрыта). */
function summaryPrice(item: CatalogViewItem | undefined, locale: string): string | null {
  if (!item) return null;
  const price = displayItemPrice(item, locale);
  if (price !== null) return price;
  return item.is_paid ? null : 'бесплатно';
}

function RequestForm({ target, locale }: { target: RequestTarget; locale: string }): JSX.Element {
  const { category, item } = target;
  const navigate = useNavigate();
  const isOther = item === undefined;
  const variants = item?.variants ?? [];
  const needsVariant =
    item !== undefined && item.price_mode === 'by_variant' && variants.length > 0;

  const schema = useMemo(
    () =>
      formSchema.superRefine((values, ctx) => {
        if (needsVariant && (values.variant_id === undefined || values.variant_id === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['variant_id'],
            message: 'Выберите вариант',
          });
        }
        if (isOther && (values.description === undefined || values.description.trim() === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['description'],
            message: 'Опишите, что нужно',
          });
        }
      }),
    [needsVariant, isOther],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: { variant_id: '', comment: '', description: '' },
  });
  const selectedVariant = watch('variant_id') ?? '';

  const [urgent, setUrgent] = useState(false);
  const { photos, addFiles, removePhoto, isUploading, uploadedKeys } = usePhotoUpload(3);

  const mutation = useCreateServiceRequest();
  const [serverErrors, setServerErrors] = useState<readonly string[]>([]);
  const [done, setDone] = useState<ServiceRequestCreatedResponse | null>(null);

  const title = item !== undefined ? item.name : category.name;
  const priceLine = summaryPrice(item, locale);

  const onSubmit = handleSubmit((values) => {
    setServerErrors([]);
    const text = isOther ? values.description : values.comment;
    const input: ServiceRequestCreateInput = {
      category_id: category.id,
      title,
      ...(item !== undefined && { item_id: ServiceItemId(item.id) }),
      ...(values.variant_id !== undefined &&
        values.variant_id !== '' && { variant_id: ServiceItemVariantId(values.variant_id) }),
      ...(text !== undefined && text.trim() !== '' && { description: text.trim() }),
      ...(urgent && { priority: 'urgent' }),
      ...(uploadedKeys.length > 0 && { photos: uploadedKeys }),
    };
    mutation.mutate(input, {
      onSuccess: (resp) => setDone(resp),
      onError: (err) => {
        setServerErrors([
          err.message !== ''
            ? err.message
            : 'Не получилось отправить заявку. Попробуйте ещё раз.',
        ]);
      },
    });
  });

  if (done) {
    /*
     * Экраны модуля заявок появились в tenant-app (features/requests):
     * предлагаем сразу открыть карточку заявки с трекингом статуса и чатом
     * (/requests/{id}, canon §17), плюс быстрый возврат к каталогу.
     */
    return (
      <section data-testid="request-success">
        <Toast message="Заявка отправлена · принято в работу" />
        <Card>
          <h2 className="hsc-card__title">Готово!</h2>
          <p>
            Мы передали Вашу заявку отелю — следите за статусом и общайтесь с нами в разделе
            «Мои заявки».
          </p>
          <div className="rq-actions">
            <Button
              data-testid="open-request"
              onClick={() => navigate(`/requests/${encodeURIComponent(done.id)}`)}
            >
              Открыть заявку
            </Button>
            <Button variant="secondary" onClick={() => navigate('/requests')}>
              Мои заявки
            </Button>
            <Button variant="ghost" onClick={() => navigate('/services')}>
              К услугам
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="request-new-page">
      <button
        type="button"
        className="ta-back"
        onClick={() => navigate(`/services/${encodeURIComponent(category.code)}`)}
      >
        ‹ {category.name}
      </button>
      <h1 className="hsc-page__title">{item !== undefined ? item.name : 'Другое — опишите'}</h1>

      {/* Summary-card услуги: название · категория · цена/бесплатно (canon §16) */}
      <div className="rq-summary" data-testid="request-summary">
        <span className="rq-summary__lbl">Услуга</span>
        <span className="rq-summary__name">
          {category.name} · {item !== undefined ? item.name : 'Другое'}
        </span>
        {priceLine !== null && <span className="rq-summary__meta">{priceLine}</span>}
      </div>

      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        {/* V5: серверные ошибки — summary-панель сверху формы */}
        {serverErrors.length > 0 && (
          <div className="ta-error-summary" role="alert" data-testid="server-error-summary">
            <strong>Не получилось отправить заявку:</strong>
            <ul>
              {serverErrors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {needsVariant && (
          <div className="ta-field">
            <span className="hsc-input__label" id="rq-variant-label">
              Вариант
            </span>
            {/* V4: inline-валидация floating tooltip'ом без layout shift */}
            {errors.variant_id && (
              <span className="ta-tooltip" role="alert" id="rq-variant-err">
                {errors.variant_id.message}
              </span>
            )}
            {/* Pill-buttons, НЕ select: цена отдельной строкой, Plex Mono, nowrap (canon §16 v5 pin) */}
            <div className="rq-pills" role="radiogroup" aria-labelledby="rq-variant-label">
              {variants.map((v) => {
                const active = selectedVariant === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={active ? 'rq-pill rq-pill--active' : 'rq-pill'}
                    data-testid={`variant-pill-${v.id}`}
                    onClick={() =>
                      setValue('variant_id', v.id, { shouldValidate: true, shouldDirty: true })
                    }
                  >
                    <span className="rq-pill__name">{v.name}</span>
                    {item?.show_price === true && (
                      <span className="rq-pill__price">{displayVariantPrice(v, locale)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isOther && (
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rq-description">
              Что нужно?
            </label>
            {errors.description && (
              <span className="ta-tooltip" role="alert" id="rq-description-err">
                {errors.description.message}
              </span>
            )}
            <textarea
              id="rq-description"
              className="ta-textarea"
              rows={4}
              placeholder="Опишите свободным текстом — мы разберёмся и поможем"
              aria-invalid={errors.description ? true : undefined}
              aria-describedby={errors.description ? 'rq-description-err' : undefined}
              {...register('description')}
            />
          </div>
        )}

        {/* Общие поля (canon §16): фото до 3 + priority toggle + комментарий */}
        <div className="ta-field">
          <span className="hsc-input__label" id="rq-photos-label">
            Фото (опц., до 3)
          </span>
          <div className="rq-photos" role="group" aria-labelledby="rq-photos-label">
            {photos.map((p) => (
              <span key={p.id} className={`rq-photo rq-photo--${p.status}`}>
                {p.previewUrl !== '' ? (
                  <img className="rq-photo__img" src={p.previewUrl} alt={p.name} />
                ) : (
                  <span className="rq-photo__name">{p.name}</span>
                )}
                <button
                  type="button"
                  className="rq-photo__remove"
                  aria-label={`Убрать фото ${p.name}`}
                  onClick={() => removePhoto(p.id)}
                >
                  ✕
                </button>
              </span>
            ))}
            {photos.length < 3 && (
              <label className="rq-photo-add" aria-label="Добавить фото">
                +
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="rq-photo-add__input"
                  data-testid="photo-input"
                  onChange={(e) => {
                    addFiles(e.currentTarget.files);
                    e.currentTarget.value = '';
                  }}
                />
              </label>
            )}
          </div>
          {photos.some((p) => p.status === 'error') && (
            <p className="rq-photo-error" role="alert">
              Часть фото не загрузилась — заявку можно отправить и без них.
            </p>
          )}
        </div>

        <div className="ta-field">
          <div className="rq-toggle-row">
            <span>
              <span className="rq-toggle-row__title" id="rq-priority-label">
                Срочно
              </span>
              <span className="rq-toggle-row__sub">Постараемся сделать в первую очередь</span>
            </span>
            {/* Pill-toggle в D5b-стиле, не нативный checkbox (canon §16 v5 pin) */}
            <button
              type="button"
              role="switch"
              aria-checked={urgent}
              aria-labelledby="rq-priority-label"
              className={urgent ? 'rq-switch rq-switch--on' : 'rq-switch'}
              data-testid="priority-toggle"
              onClick={() => setUrgent((v) => !v)}
            >
              <span className="rq-switch__thumb" />
            </button>
          </div>
        </div>

        {!isOther && (
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rq-comment">
              Комментарий (опц.)
            </label>
            {errors.comment && (
              <span className="ta-tooltip" role="alert" id="rq-comment-err">
                {errors.comment.message}
              </span>
            )}
            <textarea
              id="rq-comment"
              className="ta-textarea"
              rows={3}
              placeholder="Например: к 14:00, оставить у двери..."
              aria-invalid={errors.comment ? true : undefined}
              aria-describedby={errors.comment ? 'rq-comment-err' : undefined}
              {...register('comment')}
            />
          </div>
        )}

        <div className="rq-actions">
          {/* disabled=undefined когда фото не грузятся — иначе перетёрли бы loading-disable кнопки */}
          <Button type="submit" loading={mutation.isPending} disabled={isUploading ? true : undefined}>
            {isUploading ? 'Грузим фото…' : 'Отправить заявку'}
          </Button>
        </div>
      </form>
    </section>
  );
}

export function RequestNewPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const locale = useMemo(() => resolveGuestLocale(), []);
  const { data, isLoading, isError, refetch } = useServiceCatalog(locale);

  const serviceParam = params.get('service') ?? '';
  const categoryParam = params.get('category') ?? '';

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="request-new-skeleton">
        <Skeleton height={64} radius={14} />
        <div style={{ height: 12 }} />
        <Skeleton height={120} radius={14} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section>
        <h1 className="hsc-page__title">Новая заявка</h1>
        <Card>
          <p role="alert">Не удалось загрузить данные услуги. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
            <Button variant="ghost" onClick={() => navigate('/services')}>
              К каталогу услуг
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  let target: RequestTarget | undefined;
  if (serviceParam !== '' && serviceParam !== 'other') {
    // Deep-link на конкретную услугу: ?service={itemId}
    for (const category of data.categories) {
      const item = category.items.find((i) => i.id === serviceParam);
      if (item) {
        target = { category, item };
        break;
      }
    }
  } else {
    // «Другое»: ?category={code}&service=other — generic-форма
    const category = data.categories.find((c) => c.code === categoryParam);
    if (category) target = { category };
  }

  if (!target) {
    return (
      <section data-testid="request-target-missing">
        <h1 className="hsc-page__title">Новая заявка</h1>
        <Card>
          <p role="alert">
            Такой услуги больше нет — выберите, пожалуйста, из каталога услуг отеля.
          </p>
          <Button variant="secondary" onClick={() => navigate('/services')}>
            К каталогу услуг
          </Button>
        </Card>
      </section>
    );
  }

  return <RequestForm target={target} locale={locale} />;
}
